import { request } from './api';
import type {
  EntityType,
  ListMessagesResponse,
  Message,
  MessageMention,
  ReactionGroup,
} from '@atlas/shared';

const base = (type: EntityType, id: string) =>
  `/messaging/threads/${type}/${encodeURIComponent(id)}`;

export const messagingApi = {
  /** Initial load: thread summary + newest page (oldest->newest), and a cursor. */
  listMessages: (type: EntityType, id: string, opts: { before?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (opts.before) q.set('before', opts.before);
    if (opts.limit) q.set('limit', String(opts.limit));
    const qs = q.toString();
    return request<ListMessagesResponse>(`${base(type, id)}/messages${qs ? `?${qs}` : ''}`);
  },

  postMessage: (
    type: EntityType,
    id: string,
    input: { body: string; mentions: MessageMention[]; tempId: string },
  ) =>
    request<{ tempId?: string; message: Message }>(`${base(type, id)}/messages`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  editMessage: (messageId: string, input: { body: string; mentions: MessageMention[] }) =>
    request<Message>(`/messaging/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  deleteMessage: (messageId: string) =>
    request<undefined>(`/messaging/messages/${messageId}`, { method: 'DELETE' }),

  toggleReaction: (messageId: string, emoji: string) =>
    request<{ reactions: ReactionGroup[] }>(`/messaging/messages/${messageId}/reactions`, {
      method: 'POST',
      body: JSON.stringify({ emoji }),
    }),

  markRead: (type: EntityType, id: string, lastReadMessageId: string) =>
    request<undefined>(`${base(type, id)}/read`, {
      method: 'POST',
      body: JSON.stringify({ lastReadMessageId }),
    }),

  unreadCount: (type: EntityType, id: string) =>
    request<{ count: number }>(`${base(type, id)}/unread-count`),
};
